import { cn } from "@newxsystems/shared";
import type { CSSProperties } from "react";
import { resolveKaajPrimitive } from "../../../../client";
import { KaajSectionShell } from "../../../../modules/layout/components/KaajSectionShell/KaajSectionShell";
import { KaajAction } from "../_shared/KaajAction";
import { KaajMasonryLayout } from "./KaajMasonryLayout.client";
import type { IMasonryGalleryBlockProps } from "./MasonryGalleryBlock.props";

const Image = resolveKaajPrimitive("Image");

type MasonryGalleryTone = {
  chip: string;
  credit: string;
  frame: string;
  media: string;
  tag: string;
};

const galleryTone: MasonryGalleryTone = {
  chip: "border-[#CAD7FF] bg-[#EEF2FF] text-[#2144FF]",
  credit: "text-[#667085]",
  frame: "border-[#D9E1FF] bg-[#FAFBFF] shadow-[0_12px_34px_rgba(33,68,255,0.07)]",
  media: "bg-[#EEF2FF]",
  tag: "border-[#D9E1FF] bg-white text-[#405076]",
};

const getObjectPosition = (
  focalPoint?: NonNullable<IMasonryGalleryBlockProps["items"]>[number]["image"]["focalPoint"],
): CSSProperties | undefined =>
  focalPoint ? { objectPosition: `${focalPoint.x}% ${focalPoint.y}%` } : undefined;

export const MasonryGalleryBlock = (props: IMasonryGalleryBlockProps) => {
  const { parentClass, eyebrow, heading, subheading, items, buttons, spacing, anchorId } = props;
  const galleryItems = items ?? [];
  const actions = buttons ?? [];

  if (!eyebrow && !heading && !subheading && galleryItems.length === 0 && actions.length === 0) {
    return null;
  }

  return (
    <KaajSectionShell
      actions={
        actions.length > 0
          ? actions.map((action, index) => (
              <KaajAction
                ariaLabel={action.ariaLabel}
                href={action.href}
                key={`${action.href}-${index}`}
                label={action.text}
                target={action.target}
                variant={action.style}
              />
            ))
          : undefined
      }
      className={parentClass}
      eyebrow={eyebrow ?? undefined}
      heading={heading ?? undefined}
      headingClassName="[overflow-wrap:anywhere]"
      id={anchorId ?? undefined}
      spacing={spacing ?? "normal"}
      subheading={subheading ?? undefined}
      subheadingClassName="text-pretty"
    >
      {galleryItems.length > 0 && (
        <div className="relative w-full">
          <KaajMasonryLayout>
            {galleryItems.map((item, index) => {
              const image = item.image;

              const caption = item.caption;
              const credit = item.credit;
              const category = item.category;
              const tags = item.tags ?? [];
              const width = image.width ?? item.width ?? undefined;
              const height = image.height ?? item.height ?? undefined;
              const naturalAspectRatio =
                typeof width === "number" && width > 0 && typeof height === "number" && height > 0
                  ? `${width} / ${height}`
                  : "4 / 3";
              const mediaStyle: CSSProperties = {
                aspectRatio: item.aspectRatio ?? naturalAspectRatio,
              };
              const objectPosition = getObjectPosition(image.focalPoint);

              return (
                <article
                  className="group relative w-full pb-5 md:pb-6 xl:pb-7"
                  data-kaaj-masonry-item=""
                  key={`${image.defaultSrc ?? caption ?? "gallery-item"}-${index}`}
                >
                  <figure className="flex min-w-0 flex-col">
                    <div
                      className={cn(
                        "relative overflow-hidden rounded-[1.15rem] border p-1.5 transition-[border-color,box-shadow] duration-300 group-hover:border-[#B8C7FF] motion-reduce:transition-none",
                        galleryTone.frame,
                      )}
                    >
                      <div
                        className={cn(
                          "relative w-full overflow-hidden rounded-[0.82rem]",
                          galleryTone.media,
                        )}
                        style={mediaStyle}
                      >
                        <Image
                          alt={image.alternateText ?? ""}
                          className="relative z-10 h-full w-full object-cover transition duration-500 motion-reduce:transition-none motion-safe:group-hover:scale-[1.018]"
                          decoding="async"
                          height={height}
                          loading={index < 3 ? "eager" : "lazy"}
                          priority={index < 3}
                          sizes={
                            image.sizes ??
                            "(min-width: 1280px) 24rem, (min-width: 768px) 45vw, calc(100vw - 2.5rem)"
                          }
                          src={image.defaultSrc}
                          srcSet={image.srcSet ?? undefined}
                          style={objectPosition}
                          width={width}
                        />
                        <div className="pointer-events-none absolute inset-0 ring-1 ring-inset ring-white/25" />
                      </div>
                    </div>

                    {(caption || credit || category || tags.length > 0) && (
                      <figcaption className="flex min-w-0 flex-col gap-2.5 px-1 pt-3 text-sm sm:px-1.5">
                        {(category || tags.length > 0) && (
                          <div className="flex flex-wrap items-center gap-2">
                            {category && (
                              <span
                                className={cn(
                                  "inline-flex max-w-full rounded-full border px-2.5 py-1 text-[0.62rem] font-extrabold uppercase leading-none tracking-[0.16em] [overflow-wrap:anywhere]",
                                  galleryTone.chip,
                                )}
                              >
                                {category}
                              </span>
                            )}
                            {tags.length > 0 && (
                              <ul
                                aria-label="Image tags"
                                className="flex list-none flex-wrap gap-1.5 p-0"
                              >
                                {tags.map((tag, tagIndex) => (
                                  <li
                                    className={cn(
                                      "rounded-full border px-2 py-1 text-[0.67rem] font-semibold leading-none [overflow-wrap:anywhere]",
                                      galleryTone.tag,
                                    )}
                                    key={`${tag.tag}-${index}-${tagIndex}`}
                                  >
                                    {tag.tag}
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        )}

                        {(caption || credit) && (
                          <div className="space-y-1">
                            {caption && (
                              <p className="text-pretty text-[0.95rem] font-extrabold leading-5 text-current [overflow-wrap:anywhere]">
                                {caption}
                              </p>
                            )}
                            {credit && (
                              <p
                                className={cn("text-xs font-medium leading-5", galleryTone.credit)}
                              >
                                {credit}
                              </p>
                            )}
                          </div>
                        )}
                      </figcaption>
                    )}
                  </figure>
                </article>
              );
            })}
          </KaajMasonryLayout>
        </div>
      )}
    </KaajSectionShell>
  );
};

MasonryGalleryBlock.displayName = "Block::MasonryGalleryBlock";
